// Barrel: re-export types + lightweight metadata.
// Heavy locale dictionaries live in `./locales/*.ts` and are loaded on demand.
export type { Locale, Translation } from "./types";
import type { Locale, Translation } from "./types";

// Eagerly bundle English so the first paint never waits on a network round-trip.
import en from "./locales/en";
export { en };

// Lazy loaders for every other locale. Vite turns each dynamic import into its
// own chunk so the initial bundle only ships English (~8 KB) instead of all
// 20 languages (~140 KB).
const loaders: Record<Locale, () => Promise<{ default: Translation }>> = {
  en: () => Promise.resolve({ default: en }),
  es: () => import("./locales/es"),
  zh: () => import("./locales/zh"),
  ar: () => import("./locales/ar"),
  fr: () => import("./locales/fr"),
  pt: () => import("./locales/pt"),
  ja: () => import("./locales/ja"),
  de: () => import("./locales/de"),
  th: () => import("./locales/th"),
  hi: () => import("./locales/hi"),
  vi: () => import("./locales/vi"),
  id: () => import("./locales/id"),
  tr: () => import("./locales/tr"),
  ru: () => import("./locales/ru"),
  ko: () => import("./locales/ko"),
  it: () => import("./locales/it"),
  pl: () => import("./locales/pl"),
  nl: () => import("./locales/nl"),
  sv: () => import("./locales/sv"),
  fil: () => import("./locales/fil"),
};

const cache: Partial<Record<Locale, Translation>> = { en };

export const isLocaleLoaded = (locale: Locale) => Boolean(cache[locale]);

export const getLoadedTranslation = (locale: Locale): Translation =>
  cache[locale] ?? en;

export async function loadTranslation(locale: Locale): Promise<Translation> {
  if (cache[locale]) return cache[locale]!;
  const mod = await loaders[locale]();
  cache[locale] = mod.default;
  return mod.default;
}

// Backwards-compat: code paths that used `locale in translations` to validate
// a locale string. Only the keys matter — values are never iterated.
export const translations = Object.fromEntries(
  (Object.keys(loaders) as Locale[]).map((k) => [k, true]),
) as Record<Locale, true>;

export const localeNames: Record<Locale, string> = {
  en: "English",
  es: "Español",
  zh: "中文",
  ar: "العربية",
  fr: "Français",
  pt: "Português",
  ja: "日本語",
  ko: "한국어",
  de: "Deutsch",
  th: "ไทย",
  hi: "हिन्दी",
  vi: "Tiếng Việt",
  id: "Bahasa Indonesia",
  tr: "Türkçe",
  ru: "Русский",
  it: "Italiano",
  pl: "Polski",
  nl: "Nederlands",
  sv: "Svenska",
  fil: "Filipino",
};

export const localeFlags: Record<Locale, string> = {
  en: "🇺🇸",
  es: "🇪🇸",
  zh: "🇨🇳",
  ar: "🇸🇦",
  fr: "🇫🇷",
  pt: "🇧🇷",
  ja: "🇯🇵",
  ko: "🇰🇷",
  de: "🇩🇪",
  th: "🇹🇭",
  hi: "🇮🇳",
  vi: "🇻🇳",
  id: "🇮🇩",
  tr: "🇹🇷",
  ru: "🇷🇺",
  it: "🇮🇹",
  pl: "🇵🇱",
  nl: "🇳🇱",
  sv: "🇸🇪",
  fil: "🇵🇭",
};
